import HeadlessButton from "@components/Button";
import UpdateContractorForm from "@components/contractor/UpdateContractorForm";
import HeadlessDialog from "@components/Dialog";
import { FiEdit } from "react-icons/fi";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { buildWorkerPayload } from "@factory/buildWorkerPayload";
import { IWorkerInfoDTO } from "@interfaces/IWorkerInfoDTO";
// Deductions are now typed as IDeductionDTO[] via IWorkerInfoDTO, no direct import needed here.
import { iFormValues } from "@models/forms/iFormValues";
import axiosClient from "@service/axiosClient";
import { updateContractor } from "@service/contractorService";
import { fetchPlants, PlantOption } from "@service/PlantService";
import FacilityService, { FacilityOption } from "@service/FacilityService";
import { countries } from "@utils/countries";
import { asDate } from "@utils/date";
import { states } from "@utils/states";
import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Spinner from "@components/Spinner";
import { SnackbarContext } from "../constant_components/providers/SnackbarProvider";

// Define the validation schema for the contractor form
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  secondFirstName: Yup.string(),
  lastName: Yup.string().required("Last name is required"),
  secondLastName: Yup.string(),
  streetAddress: Yup.string().required("Street address is required"),
  city: Yup.string().required("City is required"),
  zipCode: Yup.string().required("Zip code is required"),
  state: Yup.string().required("State is required"),
  country: Yup.string().required("Country is required"),
  birthDate: Yup.date().required("Birth date is required"),
  preferredLang: Yup.string(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().required("Phone is required"),
  plant: Yup.string().required("Plant is required"),
  facility: Yup.string(),
  contractorId: Yup.string().required("Contractor ID is required"),
  firstWorkingDay: Yup.date().required("First working day is required"),
  driversLicense: Yup.string().oneOf(["yes", "no"]),
  bankAccountNumber: Yup.string(),
  employeeType: Yup.string()
    .oneOf(["W2", "1099"])
    .required("Employee type is required"),
  // payRateClass: Yup.string()
    // .oneOf(["hourly", "daily", "weekly"], "Select a valid pay rate")
    // .required("Pay rate is required"),
  // payRateStartDate: Yup.date().nullable(),
  // billingRateClass: Yup.string()
  //   .oneOf(["hourly", "daily", "weekly"], "Select a valid pay rate")
  //   .required("Pay rate is required"),
  // billingRateStartDate: Yup.date().nullable(),
  // payRate: Yup.string().required("Pay Rate is required").min(0.01),
  // billingRate: Yup.string().required("Billing Rate is required").min(0),
  taxWithHolding: Yup.string(),
  payType: Yup.string().required("Pay Type is required"),
  rentDeduction: Yup.string(),
  adminDeduction: Yup.string(),
  visaBondDeduction: Yup.string(),
  miscDeduction: Yup.string(),
  isActive: Yup.boolean(),
  engagementStatus: Yup.string()
    .required("Engagement status is required")
    .when("isActive", {
      is: true,
      then: (schema) =>
        schema.oneOf(
          ["active"],
          "When active, engagement status must be 'active'"
        ),
      otherwise: (schema) =>
        schema.test(
          "not-active",
          "When inactive, engagement status cannot be 'active'",
          (value) => value !== "active"
        ),
    }),
  engagementStartDate: Yup.date().required("Engagement start date is required"),
});

interface ContractorUpdateModalProps {
  workerId: number;
  isOpen: boolean;
  onClose: () => void;
  setIsOpen?: (v: boolean) => void;
  onHandleRefresh: () => void;
}

export default function ContractorUpdateModal({
  workerId,
  isOpen,
  onClose,
  onHandleRefresh,
}: ContractorUpdateModalProps) {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [initialValues, setInitialValues] = React.useState<iFormValues | null>(
    null
  );
  const [plants, setPlants] = React.useState<PlantOption[]>([]);
  const [facilities, setFacilities] = React.useState<FacilityOption[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const { t } = useTranslation();
  const normalizeEmployeeType = (isW2: boolean, is1099: boolean) => {
    if (isW2) return "W2";
    if (is1099) return "1099";
    return "";
  };

  React.useEffect(() => {
    if (!isOpen || !workerId) return;

    (async () => {
      setIsLoading(true);
      try {
        const [plantOptions, workerRes] = await Promise.all([
          fetchPlants(),
          axiosClient.get<IWorkerInfoDTO>(`/worker_api/info/get/${workerId}`),
        ]);
        setPlants(plantOptions);
        const w = workerRes.data;
        const matchedPlant = plantOptions.find((p) => p.id === w.plant_id);

        // Facilities are scoped to the worker's plant.
        let facilityOptions: FacilityOption[] = [];
        if (w.plant_id) {
          try {
            facilityOptions = await FacilityService.getFacilityOptionsByPlant(
              w.plant_id
            );
          } catch (facilityErr) {
            console.error("Failed to load facilities:", facilityErr);
          }
        }
        setFacilities(facilityOptions);
        const matchedFacility = facilityOptions.find(
          (f) => f.id === w.facility_id
        );

        const matchedState = states.find((s) => s.value === w.state);
        const matchedCountry = countries.find((c) => c.value === w.country);

        // find each deduction once:
        const rentObj = w.deductions?.find((d) => d.deduction_type === "rent");
        const adminObj = w.deductions?.find(
          (d) => d.deduction_type === "admin"
        );
        const visaObj = w.deductions?.find(
          (d) => d.deduction_type === "visa_bond"
        );
        const miscObj = w.deductions?.find((d) => d.deduction_type === "misc");

        const rawPayRateType = w.current_pay_rate_type?.toLowerCase() || "";
        const rawBillingRateType =
          w.current_billing_rate_type?.toLowerCase() || "";

        const payRateClass =
          rawPayRateType === "hourly"
            ? "hourly"
            : rawPayRateType === "daily"
            ? "daily"
            : rawPayRateType === "weekly"
            ? "weekly"
            : "";

        const billingRateClass =
          rawBillingRateType === "hourly"
            ? "hourly"
            : rawBillingRateType === "daily"
            ? "daily"
            : rawBillingRateType === "weekly"
            ? "weekly"
            : "";

        setInitialValues({
          firstName: w.first_name || "",
          secondFirstName: w.second_name || "",
          lastName: w.third_name || "",
          secondLastName: w.fourth_name || "",
          streetAddress: w.street || "",
          city: w.city || "",
          zipCode: w.zip || "",
          state: matchedState?.value || "",
          country: matchedCountry?.value || "",
          birthDate: w.birthdate ? new Date(w.birthdate) : new Date(),
          preferredLang: w.preferred_language || "",
          email: w.email || "",
          phone: w.phone || "",
          plant: matchedPlant ? matchedPlant.value : "",
          facility: matchedFacility ? matchedFacility.value : "",
          contractorId: String(w.contractor_id),
          firstWorkingDay: w.start_date ? new Date(w.start_date) : new Date(),
          payRateStartDate: asDate(w.current_pay_rate_start_date) ?? null,
          billingRateStartDate:
            asDate(w.current_billing_rate_start_date) ?? null,
          driversLicense: (w.drivers_license || "").toLowerCase(),
          bankAccountNumber: w.bank_account || "",
          employeeType: normalizeEmployeeType(w.is_w2, w.is_1099),
          payRateClass,
          billingRateClass,
          payRate: w.current_pay_rate != null ? String(w.current_pay_rate) : "",
          billingRate:
            w.current_billing_rate != null
              ? String(w.current_billing_rate)
              : "",
          taxWithHolding: w.tax != null ? String(w.tax * 100) : "",
          payType:
            w.pay_type === "Check"
              ? t("UpdateContractorFields.check")
              : w.pay_type === "Direct Deposit"
              ? t("UpdateContractorFields.directDeposit")
              : "",

          rentDeduction: rentObj ? String(rentObj.amount) : "",
          adminDeduction: adminObj ? String(adminObj.amount) : "",
          visaBondDeduction: visaObj ? String(visaObj.amount) : "",
          miscDeduction: miscObj ? String(miscObj.amount) : "",
          isActive: typeof w.is_active === "boolean" ? w.is_active : true,

          engagementStatus: w.engagementStatus ? w.engagementStatus : "",
          engagementStartDate: asDate(w.engagementStartDate) ?? new Date(),
        });
      } catch (err) {
        console.error("Failed to load worker or plants:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isOpen, workerId, t]);
  // console.log(
  //   "Debug: initialValues.payRateClass =",
  //   initialValues?.payRateClass,
  //   "initialValues.billingRateClass =",
  //   initialValues?.billingRateClass
  // );
  // console.log("🚀 Initial values in modal:", initialValues);

  const handleSubmit = React.useCallback(
    async (
      values: iFormValues,
      { setSubmitting }: import("formik").FormikHelpers<iFormValues>
    ) => {
      try {
        const payload = buildWorkerPayload(values, plants, facilities);
        await updateContractor(workerId, payload);
        onHandleRefresh();
        onClose();
        triggerSnackbar(`Update contractor executed successfully.`);
      } catch (err) {
        if (
          typeof err === "object" &&
          err !== null &&
          "response" in err &&
          typeof (err as any).response === "object" &&
          (err as any).response !== null &&
          "data" in (err as any).response &&
          typeof (err as any).response.data === "object" &&
          (err as any).response.data !== null &&
          "error" in (err as any).response.data
        ) {
          triggerSnackbar(
            `Update contractor failed: ${
              (err as any).response?.data?.error || err
            }`
          );
        } else {
          triggerSnackbar(`Update contractor failed: ${String(err)}`);
        }
        console.error("Update contractor failed:", err);
      } finally {
        setSubmitting(false);
      }
    },
    [plants, facilities, workerId, onHandleRefresh, onClose, triggerSnackbar]
  );

  // Reload facilities when the user changes the selected plant.
  const loadFacilities = React.useCallback(async (plantId?: number) => {
    if (!plantId) {
      setFacilities([]);
      return;
    }
    try {
      const options = await FacilityService.getFacilityOptionsByPlant(plantId);
      setFacilities(options);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      setFacilities([]);
    }
  }, []);

  if (!isOpen) return null;

  return (
    <HeadlessDialog
      title={
        <div className="tw-flex tw-items-center tw-gap-2">
          <FiEdit className="tw-text-primary-light" size={24} />
          <span>Update Contractor</span>
        </div>
      }
      description="Update contractor information below."
      isOpen={isOpen}
      setIsOpen={onClose}
      onClose={onClose}
      buttonText="Close"
      //  size="tw-max-w-[70vw]"
      size="tw-w-[100vw] xl:tw-max-w-[60vw]"
    >
      {isLoading || !initialValues ? (
        <div className="tw-p-6">Loading…</div>
      ) : (
        <ScrollableModalBody className="tw-overflow-y-scroll">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ touched, errors, values, isSubmitting, setFieldValue }) => (
              <Form className="tw-space-y-4 tw-ml-[2px]">
                <UpdateContractorForm
                  plants={plants}
                  facilities={facilities}
                  onPlantChange={(plantId) => {
                    setFieldValue("facility", "");
                    loadFacilities(plantId);
                  }}
                  touched={touched}
                  errors={errors}
                  values={values}
                  isLoading={isSubmitting}
                  success={false} // or true based on your logic
                />
                <div className="tw-flex tw-justify-end tw-mt-4">
                  <HeadlessButton
                    type="submit"
                    disabled={isSubmitting}
                    className="md:tw-w-[22%] tw-h-10"
                  >
                    {isSubmitting ? (
                      <Spinner className="tw-flex tw-justify-center tw-items-center" />
                    ) : (
                      "Update Contractor"
                    )}
                  </HeadlessButton>
                </div>
              </Form>
            )}
          </Formik>
        </ScrollableModalBody>
      )}
    </HeadlessDialog>
  );
}
